import { Fragment, useState, useRef, forwardRef, useImperativeHandle, useEffect } from "react";
import axios from "axios";
import * as PropTypes from "prop-types";
import { Col, Form, Input, message, Modal, Row, Select, Spin, Switch } from "antd";
import { debounce } from "lodash";
import moment from "moment";
import { API_ERRO_TYPE_CANCEL } from "./../../config/general";

import { teamsService, companiesService, departmentsService } from "./../../redux/services";

import {
	UIDrawerForm,
	UIFormSection,
	UIInfoBanner,
	UISwitchField,
} from "./../../components";

const formId = `form-drawer-${Math.floor(Math.random() * 10001)}`;

const renderNames = (items, emptyLabel) => items?.length
	? items.map((item) => <div key={item.id}>{item.nome}</div>)
	: emptyLabel;

const drawerHeader = {
	create: {
		title   : "Incluir novo fluxo de aprovação",
		subtitle: "Cadastre um novo fluxo e defina as empresas e departamentos que ele abrange.",
	},
	edit: {
		title   : "Editar fluxo de aprovação",
		subtitle: "Atualize as informações e a abrangência do fluxo.",
	},
	show: {
		title   : "Visualizar fluxo de aprovação",
		subtitle: "Consulte as informações e a abrangência do fluxo.",
	},
};

const ModalForm = forwardRef(({ visible, onComplete, onClose }, ref) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [uuid, setUuid] = useState(0);
	const [companies, setCompanies] = useState([]);
	const [companiesIsLoading, setCompaniesIsLoading] = useState(false);
	const [departments, setDepartments] = useState([]);
	const [departmentsIsLoading, setDepartmentsIsLoading] = useState(false);
	const [formToCompare, setFormToCompare] = useState({});
	const [data, setData] = useState([]);
	const [item, setItem] = useState({});
    const [formType, setFormType] = useState('create');

	const [form] = Form.useForm();
	const descricaoRef = useRef(null);
	const empresasRef = useRef(null);
    const formTypeRef = useRef(formType);

	const _axiosCancelCompaniesToken = useRef(null);
	const _axiosCancelDepartmentsToken = useRef(null);

    useImperativeHandle(ref, () => ({
        onOpen: (type, uuid) => {
            setFormType(type);
            onOpen(type, uuid);
        },
    }));

	const fetchCompanies = () => {
		if (_axiosCancelCompaniesToken.current) {
			_axiosCancelCompaniesToken.current.cancel("Only one request allowed at a time.");
		}

		_axiosCancelCompaniesToken.current = axios.CancelToken.source();

		setCompaniesIsLoading(true);

		companiesService.getAutocomplete({
			orderBy: "nome:asc",
			cancelToken: _axiosCancelCompaniesToken.current.token,
		})
			.then((response) => {
				setCompaniesIsLoading(false);
				setCompanies(response.data.data);
			})
			.catch((data) => {
				if (data.error_type === API_ERRO_TYPE_CANCEL) return null;

				setCompaniesIsLoading(false);

				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
				});
			});
	};

	const fetchDepartments = (value) => {
		if (_axiosCancelDepartmentsToken.current) {
			_axiosCancelDepartmentsToken.current.cancel("Only one request allowed at a time.");
		}

		_axiosCancelDepartmentsToken.current = axios.CancelToken.source();

		// if (!value.trim().length) {
			// setDepartmentsIsLoading(false);
			// setDepartments([]);
			// return false;
		// }

		setDepartmentsIsLoading(true);

		const params = {
			...(value ? { search: value } : {}),
			orderBy: "nome:asc",
			cancelToken: _axiosCancelDepartmentsToken.current.token,
			empresas_ids: (form.getFieldValue("empresas") ?? []).includes("todos") ? [] : (form.getFieldValue("empresas") ?? []),
		};

		departmentsService.getAutocomplete({ ...params })
			.then((response) => {
				setDepartmentsIsLoading(false);
				setDepartments(response.data.data);
			})
			.catch((data) => {
				if (data.error_type === API_ERRO_TYPE_CANCEL) return null;

				setDepartmentsIsLoading(false);

				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
				});
			});
	};

	const fetchDepartmentsSearch = debounce(fetchDepartments, 500);

	const onCompanyChange = (value) => {
		if (value.length > 1 && value.includes("todos")) {
			form.setFieldValue("empresas", value.filter((item) => item !== "todos"));
		}

		setDepartments([]);
		setDepartmentsIsLoading(false);

		form.setFieldValue("departamentos", ["todos"]);
		form.setFieldValue("funcionarios", ["todos"]);
	};

	const onCompanySelect = (value) => {
		if (value === "todos") {
			form.setFieldValue("empresas", ["todos"]);
		}
	};

	const onDepartmentChange = (value) => {
		if (value.length > 1 && value.includes("todos")) {
			form.setFieldValue("departamentos", value.filter((item) => item !== "todos"));
		}
	};

	const onDepartmentSelect = (value) => {
		if (value === "todos") {
			form.setFieldValue("departamentos", ["todos"]);
		}
	};

	const onOpen = (type, uuid) => {
		if (!companies.length) {
			fetchCompanies();
		}

        if (!departments.length) {
			fetchDepartments('');
		}

        if (type === 'create') return;

		setIsLoading(true);
		setUuid(uuid);

		teamsService.show({ uuid })
			.then((response) => {
				const item = response.data.data;

				setIsLoading(false);
				setItem(item);
				setDepartments(item.departamentos ?? []);
                const isShow = formType === 'show';
				fillForm(item, isShow);
			})
			.catch((data) => {
				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
					onOk: () => {
						return handleClose();
					}
				});
			});
	};

	const fillForm = (data) => {
        const isShow = formTypeRef.current === 'show';

		const formValues = {
            nome          : data.nome,
			descricao     : data.descricao,
			empresas      : isShow ? data.empresas : data.empresas.length > 0 ? data.empresas.map((item) => item.id) : ["todos"],
			departamentos : isShow ? data.departamentos : data.departamentos.length > 0 ? data.departamentos.map((item) => item.id) : ["todos"],
			ativo         : data.ativo,
		};

		setFormToCompare(formValues);
		form.setFieldsValue(formValues);
	};

	const resetFields = () => {
		form.resetFields();
		setItem({});
		setCompanies([]);
		setCompaniesIsLoading(false);
		setDepartments([]);
		setDepartmentsIsLoading(false);
	};

	const handleClose = () => {
		resetFields();
		onClose();
	};

	const checkAnyFieldHasBeenChanged = () => {
		const fieldsModified = Object.keys(formToCompare).map(field => ({
			[field]: JSON.stringify(formToCompare[field]) !== JSON.stringify(form.getFieldValue(field))
		}));

		return fieldsModified.some(value => {
			if (!value) return;
			const [fieldName] = Object.keys(value);
			return value[fieldName];
		});
	};

	const onFinish = (values) => {
		if (formType === 'edit' && !checkAnyFieldHasBeenChanged()) {
      handleClose();
      return;
    }

		setIsSending(true);

		const formData = { ...values };

		formData.uuid         = uuid;

		if (!(formData.empresas instanceof Array))     formData.empresas     = [formData.empresas];
		if (!(formData.departamentos instanceof Array)) formData.departamentos = [formData.departamentos];

		if (formData.empresas.includes("todos"))      formData.empresas      = null;
		if (formData.departamentos.includes("todos")) formData.departamentos = null;

		setData(formData);

        if (formType === 'edit') {
		    teamsService.edit(formData)
                .then(() => {
                    setIsSending(false);
                    message.success("Registro atualizado com sucesso.");
                    onComplete();
                })
                .catch((data) => {
                    setIsSending(false);

                    Modal.error({
                        title: "Ocorreu um erro!",
                        content: String(data),
                    });
                });
        } else {
            teamsService.create(formData)
                .then(() => {
                    setIsSending(false);
                    message.success("Registro cadastrado com sucesso.");
                    onComplete();
                })
                .catch((data) => {
                    setIsSending(false);

                    Modal.error({
                        title: "Ocorreu um erro!",
                        content: String(data),
                    });
                });
        }


	};

    useEffect(() => {
        formTypeRef.current = formType;
    }, [formType]);

	const isShow = formType === "show";

	return (
    <Fragment>
      <UIDrawerForm
        visible={visible}
        width={700}
        onClose={handleClose}
        isLoading={isLoading}
        isSending={isSending}
        formId={formId}
        showBtnSave={!isShow}
        icon={<i className="fal fa-project-diagram" />}
        title={drawerHeader[formType].title}
        subtitle={drawerHeader[formType].subtitle}
      >
        <Form
          form={form}
          id={formId}
          layout="vertical"
          scrollToFirstError
          onFinish={onFinish}
          initialValues={{
            ativo: true
          }}
        >
          <UIFormSection
            icon={<i className="fal fa-clipboard-list" />}
            title="Informações gerais"
            description="Dados de identificação do fluxo de aprovação.">
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="nome"
                  label="Nome"
                  hasFeedback={!isShow}
                  rules={[{ required: true, message: "Campo obrigatório." }]}
                >
                  {isShow ? (
                    formToCompare.nome ?? "-"
                  ) : (
                    <Input ref={descricaoRef} placeholder="Digite um nome" />
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="descricao"
                  label="Descrição"
                  hasFeedback={!isShow}
                  rules={[{ required: true, message: "Campo obrigatório." }]}
                >
                  {isShow ? (
                    formToCompare.descricao ?? "-"
                  ) : (
                    <Input placeholder="Digite uma descrição" />
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <UISwitchField
                  bordered
                  title="Ativo"
                  description={isShow
                    ? (item.ativo ? "Fluxo ativo e disponível para vincular usuários." : "Fluxo inativo e indisponível para vincular usuários.")
                    : "Fluxo disponível para vincular usuários."}>
                  <Form.Item name="ativo" valuePropName="checked" noStyle>
                    <Switch disabled={isShow} />
                  </Form.Item>
                </UISwitchField>
              </Col>
            </Row>
            {isShow && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col xs={24} md={12}>
                  <Form.Item label="Criação">
                    {item.created_at ? moment(item.created_at).calendar() : "-"}
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Última atualização">
                    {item.updated_at ? moment(item.updated_at).calendar() : "-"}
                  </Form.Item>
                </Col>
              </Row>
            )}
          </UIFormSection>
          <UIFormSection
            icon={<i className="fal fa-users" />}
            title="Abrangência"
            description="Empresas e departamentos aos quais os usuários vinculados a este fluxo terão acesso.">
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    name="empresas"
                    label="Empresa"
                    rules={[{ required: true, message: "Campo obrigatório." }]}
                  >
                    {isShow ? (
                      renderNames(formToCompare.empresas, "Todas")
                    ) : (
                      <Select
                        ref={empresasRef}
                        mode="multiple"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (typeof option.children === "string"
                            ? option.children
                            : option.children.props.children
                          )
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                        placeholder="Selecione a(s) empresa(s)"
                        notFoundContent={
                          companiesIsLoading ? (
                            <Spin
                              indicator={
                                <i className="fad fa-spinner-third fa-spin" />
                              }
                            />
                          ) : null
                        }
                        onChange={onCompanyChange}
                        onSelect={onCompanySelect}
                        showSearch
                      >
                        {/* <Select.Option value="todos">Todas</Select.Option> */}
                        {companies.map((item, index) => (
                          <Select.Option key={index} value={item.id}>
                            {item.nome}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="departamentos"
                    label="Departamento"
                    rules={[{ required: true, message: "Campo obrigatório." }]}
                  >
                    {isShow ? (
                      renderNames(formToCompare.departamentos, "Todos")
                    ) : (
                      <Select
                        mode="multiple"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (typeof option.children === "string"
                            ? option.children
                            : option.children.props.children
                          )
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                        placeholder="Selecione o(s) departamento(s)"
                        notFoundContent={
                          departmentsIsLoading ? (
                            <Spin
                              indicator={
                                <i className="fad fa-spinner-third fa-spin" />
                              }
                            />
                          ) : null
                        }
                        onDropdownVisibleChange={(open) => open && fetchDepartments()}
                        onSearch={fetchDepartmentsSearch}
                        onChange={onDepartmentChange}
                        onSelect={onDepartmentSelect}
                        showSearch
                      >
                        <Select.Option value="todos">Todos</Select.Option>
                        {departments.map((item, index) => (
                          <Select.Option key={index} value={item.id}>
                            {item.nome}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
          </UIFormSection>
          <UIInfoBanner
            icon={<i className="fal fa-info-circle" />}
            title="Sobre fluxos de aprovação"
            description="Cada combinação de empresas e departamentos só pode ter um fluxo ativo. Fluxos vinculados a usuários ativos não podem ser inativados ou excluídos."
          />
        </Form>
      </UIDrawerForm>
    </Fragment>
  );
});

ModalForm.propTypes = {
	visible   : PropTypes.bool.isRequired,
	onComplete: PropTypes.func.isRequired,
	onClose   : PropTypes.func.isRequired,
};

export default ModalForm;